import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/library/card";

import { useCourseForm } from "@/features/universityAndCourse/contexts/FormCourseContext";
import { Controller, useFieldArray } from "react-hook-form";
import { Plus, X } from "lucide-react";
import { useEffect } from "react";
import SearchableSelector from "@/components/ui/others/SearchableSelector";
import PercentageInput from "@/components/ui/others/PercentageInput";
import useQualification from "@/module/education/api/useQualification";

export default function CourseAdmissionRequirements() {
  const { control, watch, setError, clearErrors, errors } = useCourseForm();

  const { qualiSelects } = useQualification();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "eligibility",
  });

  // ✅ FIX: Watch 'fields.length' to ensure we always have at least 1 row
  // This runs on mount AND whenever the list becomes empty (e.g. after async data load)
  useEffect(() => {
    if (fields.length === 0) {
      append({ qualification: "", value: 0 });
    }
  }, [fields.length, append]);

  const eligibilityValues = watch("eligibility");

  const handleAddEligibility = () => {
    const lastIndex = eligibilityValues.length - 1;
    const lastItem = eligibilityValues[lastIndex];

    // ❌ Block if previous level not selected (Basic Validation)
    if (!lastItem?.qualification) {
      setError(`eligibility.${lastIndex}.qualification`, {
        type: "manual",
        message: "Please select a qualification level first",
      });
      return;
    }

    // ✅ Clear error & append
    clearErrors(`eligibility.${lastIndex}.qualification`);
    append({ qualification: "", value: 0 });
  };

  if (!qualiSelects) return null;

  return (
    <Card className="bg-white border-[#e6e8e7] w-full gap-4 p-4">
      <CardHeader className="px-0 py-0 gap-0">
        <CardTitle className="font-medium text-base text-black tracking-[-0.32px]">
          Admission Requirements
        </CardTitle>
      </CardHeader>

      <CardContent className="px-0 py-0">
        <div className="flex flex-col gap-1.5 pl-2 w-full">
          <Label className="text-[#051912] text-sm tracking-[-0.56px] font-normal leading-[1.35]">
            Eligibility
          </Label>

          <div className="flex flex-col gap-3 w-full">
            {fields.map((fieldItem, index) => {
              const qualiError =
                errors?.eligibility?.[index]?.qualification?.message;

              return (
                <div
                  key={fieldItem.id}
                  className="flex gap-2 items-start w-full"
                >
                  {/* Qualification */}
                  <div className="flex-1">
                    <Controller
                      name={`eligibility.${index}.qualification`}
                      control={control}
                      render={({ field }) => (
                        <div className="flex flex-col gap-1">
                          <SearchableSelector
                            placeholder="Search qualifications"
                            options={qualiSelects}
                            value={field.value ?? undefined}
                            onChange={(val) => {
                              field.onChange(val);
                              clearErrors(`eligibility.${index}.qualification`);
                            }}
                          />
                          {qualiError && (
                            <span className="text-xs text-red-500">
                              {qualiError}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Value */}
                  <PercentageInput
                    name={`eligibility.${index}.value`}
                    control={control}
                  />

                  {/* Remove Button */}
                  {/* Only show remove if there is more than 1 item */}
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => remove(index)}
                      className="h-10 w-10 shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            })}

            {/* Add eligibility */}
            <Button
              type="button"
              onClick={handleAddEligibility}
              className="w-full h-9 rounded-xl border border-[#add7c7] bg-gradient-to-r from-[#f0f7f5] via-[#d6ebe3] to-[#f0f7f5] hover:from-[#e6f3ed] hover:via-[#cce6dc] hover:to-[#e6f3ed] text-[#1e302a] font-medium text-sm tracking-[-0.56px] flex items-center justify-center gap-1.5 shadow-[0_0.5px_0.9px_rgba(173,215,199,1)]"
            >
              <Plus className="size-6 text-[#1e302a]" />
              Add eligibility
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
